import {getPixelsFromRem, Show, VStack} from 'platform/foundation';

import {ReactNode, useId, useMemo} from 'react';
import {OnChangeValue} from 'react-select';
import CreatableReactSelect from 'react-select/creatable';

import {defaultTo, isNil} from 'ramda';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {useComponentsTheme} from '../../../hooks/useComponentsTheme';
import {FormControlProps} from '../../../types/FormControlProps';
import {OptionTypeBase} from '../../../types/OptionTypeBase';
import {HelperText} from '../../HelperText/HelperText';
import {Label} from '../../Label/Label';
import {LabelActions} from '../../Label/LabelActions';
import {useTranslationContext} from '../../TranslationProvider/TranslationContext';
import {ChoiceCommonProps} from '../types';
import {selectComponents} from '../utils/components';
import {formatOptionFieldLabel} from '../utils/formatOptionFieldLabel';
import {getChoiceCSS} from '../utils/getChoiceCSS';
import {getFlattenedOptions} from '../utils/getFlattenedOptions';
import {reactSelectStyles} from '../utils/reactSelectStyles';
import {ChoiceProvider} from './ChoiceProvider';

export interface CreatableChoiceProps<ValueType>
  extends FormControlProps<ValueType | null>, TestIdProps, ChoiceCommonProps<ValueType, ValueType> {
  onCreateOption?: (inputValue: string) => void;
  isValidNewOption?: (inputValue: string) => boolean;
  formatCreateLabel?: (inputValue: string) => ReactNode;
  labelActions?: LabelActions;
}

export function CreatableChoice<ValueType>(props: CreatableChoiceProps<ValueType>) {
  const t = useTranslationContext();
  const theme = useComponentsTheme();
  const id = useId();

  const isInvalid = props.isInvalid || !!props.errorMessage;

  const handleChange = (options: OnChangeValue<OptionTypeBase<ValueType | Nullish>, false>) => {
    props.onChange?.(options?.value ?? null);
  };

  const getOptionsByValue = (value: ValueType | Nullish) => {
    if (isNil(value)) {
      return null;
    }

    const flatOptions = getFlattenedOptions(props.options);

    return flatOptions?.find((o) => o.value === value) ?? null;
  };

  // This is a workaround for an issue in react-select, when using custom components
  // https://github.com/JedWatson/react-select/issues/5489
  const components = useMemo(() => selectComponents<ValueType, false>(), []);

  return (
    <VStack>
      <Label
        id={id}
        isRequired={props.isRequired}
        tooltip={props.tooltip}
        isRecommended={props.isRecommended}
        data-testid={suffixTestId('label', props)}
        actions={props.labelActions}
      >
        {props.label}
      </Label>
      <ChoiceProvider
        options={{
          'data-testid': props['data-testid'],
          hasOptionCheckbox: props.hasOptionCheckbox,
          isInvalid,
        }}
      >
        <CreatableReactSelect
          inputId={id}
          formatOptionLabel={props.formatOptionLabel ?? formatOptionFieldLabel}
          defaultValue={getOptionsByValue(props.defaultValue)}
          menuPortalTarget={props.menuInPortal ? document.body : undefined}
          value={getOptionsByValue(props.value)}
          name={props.name}
          placeholder={defaultTo(t('general.labels.select'), props.placeholder)}
          onFocus={props.onFocus}
          onBlur={props.onBlur}
          onChange={handleChange}
          isDisabled={props.isDisabled}
          options={props.options}
          isClearable={!props.isNotClearable}
          isSearchable={props.isSearchable ?? true}
          components={components}
          menuPlacement={props.menuPlacement ?? 'auto'}
          maxMenuHeight={getPixelsFromRem(props.maxMenuHeight)}
          styles={reactSelectStyles<ValueType, false>(theme)}
          noOptionsMessage={props.noOptionsMessage}
          closeMenuOnScroll={props.closeMenuOnScroll}
          closeMenuOnSelect={props.closeMenuOnSelect}
          isLoading={props.isLoading}
          onCreateOption={props.onCreateOption}
          formatCreateLabel={props.formatCreateLabel}
          filterOption={props.filterOption}
          isValidNewOption={props.isValidNewOption}
          // Styling ReactSelect is problematic so we need to use the css prop
          className={getChoiceCSS(isInvalid, props.isDisabled, props.isRecommended)}
        />
      </ChoiceProvider>
      <Show when={props.errorMessage ?? props.helperText}>
        <HelperText
          errorMessage={props.errorMessage}
          helperText={props.helperText}
          data-testid={suffixTestId('helper', props)}
        />
      </Show>
    </VStack>
  );
}
